// eslint-disable-next-line spaced-comment
/// <reference types="@core/tellent-mobile-logic" />
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  type QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { startWith } from 'rxjs/operators';

import { trackByProp } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types
import { tellentOrganizationData } from '@core/tellent-data'; // FIXME
import type { Org, TellentCurrentUser, TellentMember } from '@core/tellent-types';
import { ORG_GUID, SeatKind } from '@core/tellent-types';
import {
  AvatarComponent,
  DialogService,
  DialogWidth,
  DividerComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  FormFieldComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  ScrollableDirective,
} from '@core/user-interface-legacy';

import { AddCompanyDialogComponent } from '../../add-company-dialog/add-company-dialog.component';
import { CompanySettingsAnalyticsService } from '../../services/company-settings.analytics';
import { injectTellentProfileDropdownConfig } from '../tellent-profile-dropdown.config';

@Component({
  selector: 'tla-company-select-template',
  imports: [
    DropdownItemComponent,
    AvatarComponent,
    DropdownHeaderComponent,
    IconButtonComponent,
    ScrollableDirective,
    IconComponent,
    DividerComponent,
    TranslatePipe,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './company-select-template.component.html',
  styleUrl: './company-select-template.component.less',
})
export class CompanySelectTemplateComponent {
  public readonly isMobile = window.isNativeMobileHr;

  public config = injectTellentProfileDropdownConfig();
  public dialogService = inject(DialogService);

  public orgGuid = inject(ORG_GUID);
  public analytics = inject(CompanySettingsAnalyticsService);

  public backButtonClick = output<MouseEvent>();

  public members = input.required<TellentMember[]>();

  public currentUser = input.required<TellentCurrentUser>();
  public currentCompany = input.required<Org | undefined>();
  public currentMember = input.required<TellentMember | undefined>();

  public searchControl = new FormControl('');

  public searchText = toSignal(
    this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)),
    { initialValue: this.searchControl.value },
  );

  public filteredMembers = computed(() => {
    const search = (this.searchText() ?? '').toLowerCase();
    const all = window.isNativeMobileHr
      ? this.members().filter(member =>
          member.seats.some(seat => seat.access && seat.app.kind === SeatKind.CoreHR),
        )
      : this.members();
    const filtered = search ? all.filter(m => m.org.name.toLowerCase().includes(search)) : all;
    return [...filtered].sort((a, b) =>
      a.org.name.toLowerCase().localeCompare(b.org.name.toLowerCase()),
    );
  });

  public canAddNewCompany = computed(() => {
    const currentCompany = this.currentCompany();
    return currentCompany?.canCreateOrg;
  });

  public selectedIndex: number | null = 0;
  public trackById = trackByProp<TellentMember>({ prop: 'orgGuid' });

  @ViewChild('companiesList', { read: ElementRef })
  private companiesList!: ElementRef<HTMLElement>;

  @ViewChildren('items', { read: ElementRef })
  private items!: QueryList<ElementRef<HTMLElement>>;

  constructor() {
    effect(() => {
      this.filteredMembers();
      this.selectedIndex = 0;
    });
  }

  public ngAfterViewInit() {
    this.companiesList.nativeElement.focus();
  }

  public changeCompany(member: TellentMember) {
    if (this.currentCompany()?.guid === member?.org?.guid) return;
    this.config.handlers.switchCompany(member.orgGuid);
    this.analytics.trackSignIn('admin_dropdown');
  }

  public newCompanyClick() {
    this.dialogService.openCustom(
      AddCompanyDialogComponent,
      {
        width: DialogWidth.SmallX,
        providers: [tellentOrganizationData],
      },
      {
        analyticsContext: 'admin_dropdown',
      },
    );
  }

  public manageCompaniesClick() {
    this.config.handlers.manageCompanies();
  }

  protected cancelSearch() {
    this.searchControl.setValue('');
  }

  protected onArrowDown(event: KeyboardEvent) {
    event.preventDefault();
    this.incrementSelectedIndex();
  }

  protected onArrowUp(event: KeyboardEvent) {
    event.preventDefault();
    this.decrementSelectedIndex();
  }

  protected onEnter() {
    if (this.selectedIndex != null) {
      this.changeCompany(this.filteredMembers()[this.selectedIndex]);
    }
  }

  private scrollItemIntoView(index: number): void {
    const itemEl = this.items?.toArray()[index]?.nativeElement;
    if (!itemEl) return;

    itemEl.scrollIntoView({
      behavior: 'auto',
      block: 'center',
      inline: 'nearest',
    });
  }

  private incrementSelectedIndex() {
    const list = this.filteredMembers();
    const len = list.length;
    if (len === 0 || this.selectedIndex! >= len - 1) {
      return;
    }
    this.selectedIndex!++;
    this.scrollItemIntoView(this.selectedIndex!);
  }

  private decrementSelectedIndex() {
    const list = this.filteredMembers();
    if (list.length === 0 || this.selectedIndex! <= 0) {
      return;
    }
    this.selectedIndex!--;
    this.scrollItemIntoView(this.selectedIndex!);
  }
}
